import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { Stars, Money } from "@/components/ui-veda/Primitives";
import { TID } from "@/constants/testIds";

export default function ProductDetail() {
    const { id } = useParams();
    const { requireAuth } = useAuth();
    const { addItem } = useCart();
    const [p, setP] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setNotFound(false);
        api.get(`/products/${id}`)
            .then((r) => setP(r.data))
            .catch((err) => {
                if (err?.response?.status === 404) setNotFound(true);
                else console.error(err);
            });
        api.get(`/products/${id}/reviews`)
            .then((r) => setReviews(r.data))
            .catch(() => {});
    }, [id]);

    function addToCart() {
        requireAuth(() => {
            addItem(p, 1);
            toast.success("Added to cart");
        });
    }

    async function submitReview() {
        if (!rating) {
            toast.error("Pick a star rating first.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/products/${id}/reviews`, {
                rating,
                comment: comment.trim(),
            });
            toast.success("Thanks for the review!");
            setComment("");
            setRating(0);
            const r = await api.get(`/products/${id}/reviews`);
            setReviews(r.data);
            const px = await api.get(`/products/${id}`);
            setP(px.data);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Please try again.");
        } finally {
            setSubmitting(false);
        }
    }

    if (notFound) {
        return (
            <div className="veda-container py-24 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-muted">
                    404
                </p>
                <h1 className="font-display mt-2 text-4xl text-veda-onSurface">
                    Remedy not found
                </h1>
                <p className="mt-3 text-sm text-veda-muted">
                    The remedy you're looking for doesn't exist or has been
                    removed.
                </p>
                <Link
                    to="/"
                    className="mt-6 inline-block bg-veda-inverse px-5 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-veda-onInverse"
                >
                    Back to catalogue
                </Link>
            </div>
        );
    }
    if (!p) {
        return (
            <div className="p-8 text-center text-sm text-veda-muted">Loading...</div>
        );
    }

    return (
        <div className="pb-32 md:pb-16">
            <div className="veda-container pt-6">
                <Link
                    to="/"
                    className="inline-flex items-center gap-1 text-xs font-medium text-veda-muted hover:text-veda-onSurface"
                >
                    <ArrowLeft size={14} /> Back to remedies
                </Link>
            </div>

            <div className="veda-container mt-6 grid gap-10 md:grid-cols-2 md:gap-14">
                <div className="aspect-[4/5] w-full overflow-hidden bg-veda-surface2 md:sticky md:top-24 md:h-[calc(100vh-8rem)]">
                    <img
                        src={p.image_url}
                        alt={p.name}
                        className="h-full w-full object-cover"
                    />
                </div>
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-brand">
                        {p.category}
                    </p>
                    <h1 className="font-display mt-1 text-3xl text-veda-onSurface md:text-5xl">
                        {p.name}
                    </h1>
                    {p.company && (
                        <p
                            data-testid={TID.productDetailCompany}
                            className="mt-1 text-sm text-veda-muted"
                        >
                            by <span className="text-veda-onSurface">{p.company}</span>
                        </p>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                        <Stars value={p.rating || 0} size={13} />
                        <span className="text-xs text-veda-muted">
                            {p.rating_count || 0} reviews
                        </span>
                        <span className="text-xs text-veda-muted">·</span>
                        <span className="text-xs text-veda-muted">
                            {p.stock > 0 ? `${p.stock} in stock` : "Out of stock"}
                        </span>
                    </div>
                    <p className="mt-6 font-display text-4xl text-veda-onSurface">
                        <Money value={p.price} />
                    </p>
                    <p className="mt-4 text-sm leading-relaxed text-veda-onSurface/85 md:text-base">
                        {p.description}
                    </p>

                    {/* Desktop-only Add to cart (inline, replacing the sticky bar on wider screens) */}
                    <button
                        data-testid={`${TID.addToCartBtn}-desktop`}
                        onClick={addToCart}
                        disabled={p.stock <= 0}
                        className="mt-8 hidden w-full max-w-sm bg-veda-inverse px-4 py-3.5 text-sm font-semibold uppercase tracking-[0.16em] text-veda-onInverse hover:opacity-95 disabled:opacity-50 md:inline-block"
                    >
                        {p.stock <= 0 ? "Out of stock" : "Add to cart"}
                    </button>

                    {[
                        ["Ingredients", p.ingredients],
                        ["Benefits", p.benefits],
                        ["Dosage", p.dosage],
                    ].map(([label, val]) =>
                        val ? (
                            <div
                                key={label}
                                className="mt-6 border-t border-veda-divider pt-5"
                            >
                                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                    {label}
                                </p>
                                <p className="mt-2 text-sm leading-relaxed text-veda-onSurface/85">
                                    {val}
                                </p>
                            </div>
                        ) : null,
                    )}

                    {/* Reviews */}
                    <div className="mt-10 border-t border-veda-divider pt-8">
                        <div className="flex items-baseline justify-between">
                            <p className="font-display text-2xl text-veda-onSurface">
                                Reviews
                            </p>
                            <p className="text-xs text-veda-muted">
                                {reviews.length} total
                            </p>
                        </div>
                        <div className="mt-4 border border-veda-border bg-veda-surface p-4">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                Rate this remedy
                            </p>
                            <div className="mt-2 flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button
                                        key={n}
                                        data-testid={n === 5 ? TID.rateBtn : undefined}
                                        onClick={() =>
                                            requireAuth(() => setRating(n))
                                        }
                                        aria-label={`Rate ${n} stars`}
                                    >
                                        <Star
                                            size={22}
                                            strokeWidth={1.5}
                                            className={
                                                n <= rating
                                                    ? "fill-veda-warning text-veda-warning"
                                                    : "text-veda-borderStrong"
                                            }
                                        />
                                    </button>
                                ))}
                            </div>
                            <textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                onFocus={(e) => {
                                    if (rating === 0)
                                        requireAuth(() => e.target.blur());
                                }}
                                rows={3}
                                placeholder="Share a few words..."
                                className="mt-3 w-full border border-veda-border bg-veda-surface p-3 text-sm focus:border-veda-brand focus:outline-none"
                            />
                            <button
                                onClick={() => requireAuth(submitReview)}
                                disabled={submitting}
                                className="mt-3 bg-veda-brand px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-white disabled:opacity-70"
                            >
                                {submitting ? "Sending..." : "Post review"}
                            </button>
                        </div>

                        <ul className="mt-4 divide-y divide-veda-divider">
                            {reviews.length === 0 && (
                                <li className="py-4 text-sm text-veda-muted">
                                    No reviews yet. Be the first!
                                </li>
                            )}
                            {reviews.map((r) => (
                                <li key={r.review_id} className="py-4">
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm font-semibold text-veda-onSurface">
                                            {r.user_name}
                                        </p>
                                        <Stars value={r.rating} size={11} />
                                    </div>
                                    {r.comment && (
                                        <p className="mt-1 text-sm text-veda-onSurface/80">
                                            {r.comment}
                                        </p>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Sticky bottom CTA — mobile only */}
            <div className="fixed inset-x-0 bottom-0 z-20 border-t border-veda-border bg-veda-surface md:hidden">
                <div className="veda-container py-3">
                    <button
                        data-testid={TID.addToCartBtn}
                        onClick={addToCart}
                        disabled={p.stock <= 0}
                        className="w-full bg-veda-inverse px-4 py-3 text-sm font-semibold uppercase tracking-[0.16em] text-veda-onInverse hover:opacity-95 disabled:opacity-50"
                    >
                        {p.stock <= 0 ? "Out of stock" : "Add to cart"}
                    </button>
                </div>
            </div>
        </div>
    );
}
